<template>
  <div
    class="trail-stats-overlay sticky top-0 z-20 backdrop-blur-sm border-b px-4 py-2"
    :class="dark
      ? 'bg-slate-900/90 border-slate-700 text-slate-100'
      : 'bg-white/80 border-gray-200'"
  >
    <div class="flex items-center justify-between gap-3 max-w-lg mx-auto">
      <!-- Streak -->
      <div class="flex items-center gap-1 text-sm">
        <MTIcon name="flame" :size="18" class="text-orange-500" />
        <span class="font-semibold">{{ streak }}</span>
        <span
          class="hidden sm:inline text-xs"
          :class="dark ? 'text-slate-400' : 'text-[var(--mt-text-muted)]'"
        >day streak</span>
        <span
          v-if="activeToday"
          class="hidden sm:inline text-[8px] px-1.5 py-0.5 rounded-full leading-none"
          :class="dark ? 'text-green-300 bg-green-900/50' : 'text-green-700 bg-green-100'"
        >Active</span>
      </div>

      <!-- XP -->
      <div class="flex items-center gap-1.5">
        <MTIcon name="star" :size="16" :class="dark ? 'text-purple-400' : 'text-purple-500'" />
        <span class="text-sm font-semibold">{{ xp }} XP</span>
      </div>

      <!-- Tasks toggle -->
      <button
        class="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full transition-colors"
        :class="dark
          ? (showTasks ? 'bg-purple-900/60 text-purple-300' : 'bg-slate-800 text-slate-300 hover:bg-slate-700')
          : (showTasks ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')"
        @click="showTasks = !showTasks"
      >
        <MTIcon name="list-todo" :size="16" />
        <span class="hidden sm:inline">Tasks</span>
        <span
          v-if="pendingTaskCount > 0"
          class="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-purple-500 text-white"
        >{{ pendingTaskCount }}</span>
      </button>
    </div>

    <!-- Expandable tasks panel -->
    <div
      class="overflow-hidden transition-all duration-300 max-w-lg mx-auto"
      :style="{ maxHeight: showTasks ? '400px' : '0' }"
    >
      <div class="pt-3 pb-1">
        <p
          v-if="statusMessage"
          class="text-xs mb-2"
          :class="dark ? 'text-slate-400' : 'text-[var(--mt-text-muted)]'"
        >{{ statusMessage }}</p>
        <div v-if="tasks.length" class="task-grid-compact">
          <TaskCard
            v-for="task in tasks"
            :key="task.type + (task.lessonId || '')"
            :task="task"
            @start="$emit('task-start', task)"
          />
        </div>
        <div v-else class="text-center py-3 text-green-600 text-sm font-medium">
          <MTIcon name="party-popper" :size="20" class="inline mr-1" />
          All done for today!
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import MTIcon from '@/components/ui/MTIcon.vue';
import TaskCard from '@/components/learning/TaskCard.vue';

const props = defineProps({
  streak: { type: Number, default: 0 },
  activeToday: { type: Boolean, default: false },
  xp: { type: Number, default: 0 },
  tasks: { type: Array, default: () => [] },
  totalXP: { type: Number, default: 0 },
  statusMessage: { type: String, default: '' },
  dark: { type: Boolean, default: false }
});

defineEmits(['task-start']);

const showTasks = ref(false);

const pendingTaskCount = computed(() =>
  props.tasks.filter(t => !t.completed).length
);
</script>

<style scoped>
.task-grid-compact {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 480px) {
  .task-grid-compact {
    grid-template-columns: 1fr;
  }
}
</style>
